import axios from "axios";
import {
  ADMIN_API_URL,
  API_BASE_URL,
  API_URL,
  LANDING_API_URL,
  getDefaultHeaders,
} from "./config";

/**
 * Runtime HTTP/HTTPS switch for API calls, driven by the super-admin
 * "Allow HTTP access" general setting (`allow_http_access`).
 *
 *   ON  -> our API hosts are called over http://   (e.g. while the SSL certificate is expired)
 *   OFF -> our API hosts are called over https:// only
 *
 * The API base URLs come from REACT_APP_* env vars, which are baked in at build
 * time, so the scheme is swapped per request instead of editing the env. The
 * last known value is kept in localStorage so the next page load starts on the
 * right scheme before the settings have been fetched again.
 *
 * Note that http://dev... and https://dev... are different origins, so they do
 * NOT share localStorage/sessionStorage. An https page whose API certificate has
 * expired therefore can't rely on a value stored by the http page; it detects
 * the failed request instead and moves itself to http (see handleNetworkFailure).
 *
 * Every XMLHttpRequest (which axios uses for all its instances) and every
 * fetch() is rewritten here, so separate axios instances (upload, admin, embed)
 * and plain fetch/XHR uploads follow the setting without extra wiring.
 *
 * Only hosts of our own API are touched, and local/private hosts are never
 * rewritten, so `http://localhost:8080` keeps working in development.
 */

const STORAGE_KEY = "allowHttpAccess";

const LOCAL_HOST_PATTERN =
  /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|.+\.local$)/i;

const parseUrl = (value) => {
  try {
    return new URL(String(value));
  } catch (e) {
    return null;
  }
};

const isLocalHost = (host) => LOCAL_HOST_PATTERN.test(String(host || ""));

const MANAGED_HOSTS = new Set(
  [API_BASE_URL, API_URL, ADMIN_API_URL, LANDING_API_URL]
    .map((value) => parseUrl(value)?.hostname)
    .filter((host) => host && !isLocalHost(host)),
);

const isOurHost = (host) => {
  const hostname = String(host || "").toLowerCase();
  if (!hostname || isLocalHost(hostname)) return false;
  if (MANAGED_HOSTS.has(hostname)) return true;
  return hostname === "haatsign.sa" || hostname.endsWith(".haatsign.sa");
};

/** true = HTTP allowed, false = HTTPS only, null = not known yet. */
export const getStoredHttpAccess = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch (e) {
    // localStorage can be blocked (private mode); behave as "unknown".
  }
  return null;
};

const storeHttpAccess = (allowed) => {
  try {
    localStorage.setItem(STORAGE_KEY, allowed ? "1" : "0");
  } catch (e) {
    // Non-fatal: the next load just falls back to probing again.
  }
};

const pageIsHttps = () =>
  typeof window !== "undefined" && window.location?.protocol === "https:";

/**
 * The scheme this page was opened with is itself the backend's answer.
 *
 * The backend builds every link it hands out (embed / mobile webview URLs) from
 * the "Allow HTTP access" setting: http:// when it is ON, https:// when it is OFF.
 * So a page that is being served over plain http:// from a real (non-local) host
 * can only exist while HTTP access is ON. A fresh webview has no localStorage
 * value yet (and may still hold a stale one from before the setting changed), so
 * without this its API calls would stay on https:// and hit the expired
 * certificate. Returns true in that case, otherwise null (= not inferable).
 */
const httpAccessFromPage = () => {
  if (typeof window === "undefined" || !window.location) {
    return null;
  }
  const { protocol, hostname } = window.location;
  return protocol === "http:" && !isLocalHost(hostname) ? true : null;
};

/** Page scheme first, then the last value stored from the general settings. */
const getHttpAccess = () => httpAccessFromPage() ?? getStoredHttpAccess();

/**
 * Rewrites the scheme of an absolute URL that points at one of our API hosts.
 * Anything else (relative URLs, third-party hosts, localhost) is returned as is.
 */
export const applyApiScheme = (url) => {
  if (typeof url !== "string" || !/^https?:\/\//i.test(url)) {
    return url;
  }

  const allowed = getHttpAccess();
  if (allowed === null) {
    return url;
  }

  const parsed = parseUrl(url);
  const pageHost =
    typeof window !== "undefined" ? window.location?.hostname : "";
  const isPageHost =
    !!pageHost && parsed?.hostname === pageHost && !isLocalHost(pageHost);
  if (!parsed || (!isOurHost(parsed.hostname) && !isPageHost)) {
    return url;
  }

  if (allowed) {
    // An https page can't call http endpoints (mixed content), so leave it.
    return pageIsHttps() ? url : url.replace(/^https:\/\//i, "http://");
  }

  return url.replace(/^http:\/\//i, "https://");
};

// ── Page scheme switching ────────────────────────────────────────────────────

const REDIRECT_GUARD_KEY = "httpAccessRedirected";
const REDIRECT_GUARD_MS = 60 * 1000;

/**
 * At most one scheme switch per minute per tab, so a server that bounces the
 * page back cannot cause a redirect loop, while a later visit in the same tab
 * can still switch (a one-time-per-session flag would block it forever).
 */
const claimRedirect = () => {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY) || 0);
    if (Date.now() - last < REDIRECT_GUARD_MS) return false;
    sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Moves a page of our app onto the given scheme ("http:" or "https:").
 * Skipped inside iframes (an https parent would block an http frame), on
 * local hosts, and when the page is already on that scheme.
 */
const redirectPageToScheme = (protocol) => {
  if (typeof window === "undefined" || !window.location) {
    return;
  }

  const { hostname, href, protocol: current } = window.location;
  if (
    current === protocol ||
    !isOurHost(hostname) ||
    window.top !== window.self
  ) {
    return;
  }

  if (!claimRedirect()) return;

  window.location.replace(href.replace(/^https?:/i, protocol));
};

/**
 * While HTTP access is ON, move an https page of our app onto http:// so its
 * absolute API calls (e.g. the upload host) can be rewritten to http too.
 */
const redirectPageToHttp = () => redirectPageToScheme("http:");

/**
 * A request to one of our API hosts got no response at all from an https page:
 * almost always an expired certificate on that host. The page can't retry over
 * http (mixed content), so it moves itself to http. There the settings are read
 * again, and if HTTP access is OFF the page is sent back to https.
 */
const handleNetworkFailure = (url) => {
  if (!pageIsHttps()) return;

  const target = parseUrl(url);
  if (!target || !isOurHost(target.hostname)) {
    return;
  }

  // The admin API is on the same host as this page, and that certificate is
  // expired, so a failure here is the certificate problem. Move to http and
  // let /admin/get-general-settings decide whether to stay there.
  redirectPageToHttp();
};

/** Axios error version of handleNetworkFailure. */
export const handleApiNetworkFailure = (error) => {
  if (error?.response || error?.code !== "ERR_NETWORK" || !error?.config) {
    return;
  }

  try {
    handleNetworkFailure(axios.getUri(error.config));
  } catch (e) {
    // Not a resolvable URL; nothing to do.
  }
};

// ── Asset URL rewriting in API payloads ──────────────────────────────────────

const IMAGE_EXT_PATTERN =
  /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)(?:$|[?#])/i;

const IMAGE_FIELD_KEYS = new Set([
  "file_url",
  "image_url",
  "imageurl",
  "logo",
  "logo_url",
  "logopreview",
  "workflow_theme_logo",
  "maintenance_image_en",
  "maintenance_image_ar",
  "page_not_found_image",
  "qr_code",
  "qr_image",
  "qrimage",
  "avatar",
  "profile_image",
  "profile_photo",
  "thumbnail",
  "preview_url",
]);

const isAssetUrl = (url) => {
  if (typeof url !== "string" || url.length > 2048 || !/^https?:\/\//i.test(url)) {
    return false;
  }

  const parsed = parseUrl(url);
  if (!parsed) return false;

  return (
    IMAGE_EXT_PATTERN.test(`${parsed.pathname}${parsed.search}`) ||
    /\/storage\//i.test(parsed.pathname)
  );
};

const rewriteAssetUrl = (url) => (isAssetUrl(url) ? applyApiScheme(url) : url);

const rewriteAssetUrlsInPayload = (value, seen = new WeakSet()) => {
  if (typeof value === "string") {
    return rewriteAssetUrl(value);
  }

  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return value;
  if (typeof Blob !== "undefined" && value instanceof Blob) return value;
  if (typeof ArrayBuffer !== "undefined" && value instanceof ArrayBuffer) {
    return value;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      value[index] = rewriteAssetUrlsInPayload(value[index], seen);
    }
    return value;
  }

  Object.keys(value).forEach((key) => {
    const current = value[key];
    if (
      typeof current === "string" &&
      IMAGE_FIELD_KEYS.has(String(key).toLowerCase())
    ) {
      value[key] = applyApiScheme(current);
      return;
    }

    value[key] = rewriteAssetUrlsInPayload(current, seen);
  });

  return value;
};

/** Rewrite image and storage URLs inside an API payload. */
export const applyAssetSchemeToResponse = (response) => {
  const responseType = response?.config?.responseType;
  if (responseType === "blob" || responseType === "arraybuffer") {
    return response;
  }

  if (response?.data && typeof response.data === "object") {
    rewriteAssetUrlsInPayload(response.data);
  }

  return response;
};

// ── Browser-level rewrites (img, a, fetch, XHR) ──────────────────────────────

/**
 * URLs that must go out exactly as written (the deliberate "other scheme"
 * settings probe below), so the fetch/XHR rewrites leave them alone.
 */
const SCHEME_BYPASS_URLS = new Set();

const rewriteRequestUrl = (url) =>
  SCHEME_BYPASS_URLS.has(url) ? url : applyApiScheme(url);

const toUrlString = (value) => {
  if (typeof value === "string") return value;
  if (typeof URL !== "undefined" && value instanceof URL) return value.href;
  return null;
};

const installBrowserAssetRewrites = () => {
  if (typeof window === "undefined" || window.__haatsignAssetScheme) {
    return;
  }

  window.__haatsignAssetScheme = true;

  let srcProto = HTMLImageElement.prototype;
  let srcDescriptor = null;
  while (srcProto) {
    srcDescriptor = Object.getOwnPropertyDescriptor(srcProto, "src");
    if (srcDescriptor?.set && srcDescriptor?.get) break;
    srcDescriptor = null;
    srcProto = Object.getPrototypeOf(srcProto);
  }

  if (srcDescriptor?.set && srcDescriptor?.get) {
    Object.defineProperty(HTMLImageElement.prototype, "src", {
      configurable: true,
      enumerable: srcDescriptor.enumerable,
      get: srcDescriptor.get,
      set(value) {
        const next = typeof value === "string" ? applyApiScheme(value) : value;
        srcDescriptor.set.call(this, next);
      },
    });
  }

  let hrefProto = HTMLAnchorElement.prototype;
  let hrefDescriptor = null;
  while (hrefProto) {
    hrefDescriptor = Object.getOwnPropertyDescriptor(hrefProto, "href");
    if (hrefDescriptor?.set && hrefDescriptor?.get) break;
    hrefDescriptor = null;
    hrefProto = Object.getPrototypeOf(hrefProto);
  }

  if (hrefDescriptor?.set && hrefDescriptor?.get) {
    Object.defineProperty(HTMLAnchorElement.prototype, "href", {
      configurable: true,
      enumerable: hrefDescriptor.enumerable,
      get: hrefDescriptor.get,
      set(value) {
        const next = typeof value === "string" ? rewriteAssetUrl(value) : value;
        hrefDescriptor.set.call(this, next);
      },
    });
  }

  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttribute(name, value) {
    const attributeName = String(name).toLowerCase();
    if (typeof value === "string") {
      if (this instanceof HTMLImageElement && attributeName === "src") {
        return originalSetAttribute.call(this, name, applyApiScheme(value));
      }

      if (this instanceof HTMLAnchorElement && attributeName === "href") {
        return originalSetAttribute.call(this, name, rewriteAssetUrl(value));
      }
    }

    return originalSetAttribute.call(this, name, value);
  };

  // fetch(): every call to our API hosts follows the setting (uploads included),
  // and a call that gets no response triggers the http fallback.
  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      let request = input;
      let url = toUrlString(input);

      if (url !== null) {
        url = rewriteRequestUrl(url);
        request = url;
      } else if (typeof Request !== "undefined" && input instanceof Request) {
        url = rewriteRequestUrl(input.url);
        if (url !== input.url) {
          request = new Request(url, input);
        }
      }

      return originalFetch(request, init).catch((error) => {
        // fetch rejects only on network failure (or abort), never on 4xx/5xx.
        if (error?.name !== "AbortError" && url) {
          handleNetworkFailure(url);
        }
        throw error;
      });
    };
  }

  // XMLHttpRequest: axios uses it for every instance it creates, so this covers
  // upload/admin/embed instances that don't have the axios interceptors below,
  // as well as hand-written XHR uploads with progress bars.
  if (typeof XMLHttpRequest !== "undefined") {
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
      const urlString = toUrlString(url);
      const nextUrl = urlString !== null ? rewriteRequestUrl(urlString) : url;

      if (typeof nextUrl === "string" && !this.__haatsignFailureHook) {
        this.__haatsignFailureHook = true;
        // "error" fires only when no response arrived at all (TLS/DNS/CORS).
        this.addEventListener("error", () => handleNetworkFailure(this.__haatsignUrl));
      }
      this.__haatsignUrl = nextUrl;

      return originalOpen.call(this, method, nextUrl, ...rest);
    };
  }
};

installBrowserAssetRewrites();

/** Axios request-config version of applyApiScheme. */
export const applyApiSchemeToConfig = (config) => {
  if (!config) {
    return config;
  }

  config.baseURL = applyApiScheme(config.baseURL);
  config.url = applyApiScheme(config.url);
  return config;
};

// ── General settings ─────────────────────────────────────────────────────────

/**
 * Persist the value coming from the general-settings response and put the page
 * on the matching scheme: http:// while HTTP access is ON, https:// when OFF.
 */
export const syncHttpAccessFromSettings = (settings) => {
  if (!settings || settings.allow_http_access === undefined) {
    return;
  }

  const allowed =
    settings.allow_http_access === true ||
    Number(settings.allow_http_access) === 1;

  storeHttpAccess(allowed);
  redirectPageToScheme(allowed ? "http:" : "https:");
};

/**
 * Fallback for when the settings request failed with no response at all
 * (typically an expired/invalid certificate): retry once over the other
 * scheme. The backend's answer decides which scheme we keep using.
 *
 * This must be the same call the admin app makes on startup:
 * GET {API_BASE_URL}/admin/get-general-settings, whose `data.allow_http_access`
 * is the switch.
 */
export const fetchGeneralSettingsOverOtherScheme = async () => {
  const currentBase = applyApiScheme(API_BASE_URL);
  const parsed = parseUrl(currentBase);

  if (!parsed || !MANAGED_HOSTS.has(parsed.hostname)) {
    return null;
  }

  const otherProtocol = parsed.protocol === "https:" ? "http:" : "https:";
  if (otherProtocol === "http:" && pageIsHttps()) {
    // An https page can't ask over http (mixed content), so move the page to
    // http. There the settings are read again, and if HTTP access is OFF the
    // page is sent back to https.
    redirectPageToHttp();
    return null;
  }

  const otherBase = currentBase.replace(/^https?:/i, otherProtocol);
  const settingsUrl = `${otherBase}/admin/get-general-setting`;

  // A bare axios instance (no interceptors) plus the bypass list, so the URL is
  // not rewritten back to the scheme that just failed.
  SCHEME_BYPASS_URLS.add(settingsUrl);
  try {
    const response = await axios.create().get(settingsUrl, {
      headers: getDefaultHeaders(),
      timeout: 8000,
    });
    return response?.data?.data ?? null;
  } catch (e) {
    return null;
  } finally {
    SCHEME_BYPASS_URLS.delete(settingsUrl);
  }
};

// Last known setting was ON: leave https before the settings are fetched again.
if (getStoredHttpAccess() === true) {
  redirectPageToHttp();
}

// Raw `axios.get(...)` calls (outside axiosInstance) get the same treatment.
axios.interceptors.request.use(applyApiSchemeToConfig);
axios.interceptors.response.use(
  (response) => applyAssetSchemeToResponse(response),
  (error) => {
    handleApiNetworkFailure(error);
    return Promise.reject(error);
  },
);
